const OIDPID_STORAGE_KEY = 'smOverriddenOidcDefaultProviderId';
const CLEAR_IDENTITY_PROVIDER_ID = 'clr';

/**
 * Gets the oidpid value from URL query parameter
 */
export function getOidpidFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('oidpid');
}

/**
 * Gets the stored oidpid from localStorage
 */
export function getStoredOidpid(): string | null {
  return localStorage.getItem(OIDPID_STORAGE_KEY);
}

/**
 * Stores the oidpid in localStorage
 */
export function setStoredOidpid(oidpid: string): void {
  localStorage.setItem(OIDPID_STORAGE_KEY, oidpid);
}

/**
 * Clears the stored oidpid from localStorage
 */
export function clearStoredOidpid(): void {
  localStorage.removeItem(OIDPID_STORAGE_KEY);
}

/**
 * Validates if the provided oidpid is allowed
 */
export function isAllowedIdentityProviderId(idp: string, allowedProviders: string[]): boolean {
  // Long strings can be DoS risk
  const input = idp && idp.length < 100 ? encodeURIComponent(idp) : undefined;
  return (
    idp === CLEAR_IDENTITY_PROVIDER_ID ||
    (!!input && allowedProviders?.length > 0 && allowedProviders.indexOf(idp) !== -1)
  );
}

/**
 * Checks if the oidpid is the clear command
 */
export function isClearIdentityProviderId(idp: string | null): boolean {
  return idp === CLEAR_IDENTITY_PROVIDER_ID;
}

/**
 * Resolves the effective oidpid based on URL param, storage, and allowed list
 * @param allowedProviders - List of allowed provider IDs from tenant config
 * @returns The provider id to use, or null for default behavior
 */
export function resolveOidpid(allowedProviders: string[]): string | null {
  const urlOidpid = getOidpidFromUrl();

  if (isClearIdentityProviderId(urlOidpid)) {
    clearStoredOidpid();
    return null;
  }

  // If URL has valid oidpid, store and use it
  if (urlOidpid && isAllowedIdentityProviderId(urlOidpid, allowedProviders)) {
    setStoredOidpid(urlOidpid);
    return urlOidpid;
  }

  // Fall back to stored value if still valid
  const storedOidpid = getStoredOidpid();
  if (storedOidpid && isAllowedIdentityProviderId(storedOidpid, allowedProviders)) {
    return storedOidpid;
  }

  return null;
}
